import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import Layout from '../components/Layout';
import './MonitoringDashboard.css';

const MonitoringDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [notifications, setNotifications] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [billing, setBilling] = useState(null);
  
  // Pagination
  const [logsPage, setLogsPage] = useState(1);
  const [logsPagination, setLogsPagination] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [dateRange, activeTab, logsPage]);

  const fetchData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const params = { startDate, endDate };

      if (activeTab === 'overview') {
        const res = await api.get('/api/monitoring/dashboard/overview', { params });
        setOverview(res.data.data);
        
        // Also fetch system status
        const sysRes = await api.get('/api/monitoring/system/status');
        setSystemStatus(sysRes.data.data);
      } else if (activeTab === 'logs') {
        const res = await api.get('/api/monitoring/logs', { 
          params: { ...params, page: logsPage, limit: 50 }
        });
        setLogs(res.data.data.logs);
        setLogsPagination(res.data.data.pagination);
      } else if (activeTab === 'notifications') {
        const res = await api.get('/api/monitoring/notifications/analytics', { params });
        setNotifications(res.data.data);
      } else if (activeTab === 'analytics') {
        const [platformRes, buyersRes, sellersRes] = await Promise.all([
          api.get('/api/monitoring/analytics/platform', { params }),
          api.get('/api/monitoring/analytics/buyers', { params: { ...params, limit: 10 } }),
          api.get('/api/monitoring/analytics/sellers', { params: { ...params, limit: 10 } })
        ]);
        setAnalytics({
          platform: platformRes.data.data,
          buyers: buyersRes.data.data,
          sellers: sellersRes.data.data
        });
      } else if (activeTab === 'jobs') {
        const res = await api.get('/api/monitoring/jobs/status');
        setJobs(res.data.data);
      } else if (activeTab === 'billing') {
        const res = await api.get('/api/monitoring/billing/metrics', { params });
        setBilling(res.data.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to fetch monitoring data');
      }
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    
    switch (dateRange) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 30);
    }
    
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  return (
    <Layout>
      <div className="monitoring-dashboard">
        {/* Header */}
        <div className="monitoring-header">
          <div className="monitoring-title">
            <h1>📊 System Monitoring & Analytics</h1>
            <p>Real-time monitoring, analytics, and usage-based billing metrics</p>
          </div>
          
          <div className="monitoring-controls">
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="date-range-select"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <button onClick={fetchData} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="monitoring-tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📈 Overview
          </button>
          <button
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📝 Action Logs
          </button>
          <button
            className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            📧 Notifications
          </button>
          <button
            className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            📊 Analytics
          </button>
          <button
            className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            ⚙️ Background Jobs
          </button>
          <button
            className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            💰 Billing Metrics
          </button>
        </div>

        {/* Content */}
        <div className="monitoring-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading monitoring data...</p>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && overview && (
                <OverviewTab data={overview} systemStatus={systemStatus} />
              )}
              {activeTab === 'logs' && (
                <LogsTab 
                  logs={logs} 
                  pagination={logsPagination}
                  page={logsPage}
                  setPage={setLogsPage}
                />
              )}
              {activeTab === 'notifications' && notifications && (
                <NotificationsTab data={notifications} />
              )}
              {activeTab === 'analytics' && analytics && (
                <AnalyticsTab data={analytics} />
              )}
              {activeTab === 'jobs' && jobs && (
                <JobsTab data={jobs} />
              )}
              {activeTab === 'billing' && billing && (
                <BillingTab data={billing} />
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Overview Tab Component
const OverviewTab = ({ data, systemStatus }) => {
  // Add safety checks for data
  if (!data || !data.metrics) {
    return <div className="empty-state">No overview data available</div>;
  }

  const metrics = data.metrics;
  const notifByChannel = Object.entries(data.notificationsByChannel || {});
  const topEndpoints = data.topApiEndpoints || [];

  return (
    <div className="overview-tab">
      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <MetricCard
          icon="🔌"
          title="API Calls"
          value={metrics.totalApiCalls || 0}
          color="#0088FE"
        />
        <MetricCard
          icon="📧"
          title="Notifications Sent"
          value={metrics.totalNotifications || 0}
          color="#00C49F"
        />
        <MetricCard
          icon="🛍️"
          title="Total Orders"
          value={metrics.totalOrders || 0}
          color="#FFBB28"
        />
        <MetricCard
          icon="💰"
          title="Total Revenue"
          value={`$${metrics.totalRevenue || '0.00'}`}
          color="#FF8042"
        />
        <MetricCard
          icon="👥"
          title="Total Users"
          value={metrics.totalUsers || 0}
          color="#8884d8"
        />
        <MetricCard
          icon="✅"
          title="System Status"
          value={systemStatus?.status || 'Unknown'}
          color="#82ca9d"
          subtitle={systemStatus?.uptime ? `Uptime: ${Math.floor(systemStatus.uptime / 3600)}h` : 'N/A'}
        />
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Notifications by Channel */}
        <div className="chart-card">
          <h3>📊 Notifications by Channel</h3>
          {notifByChannel.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={notifByChannel.map(([channel, count]) => ({
                    name: channel,
                    value: count
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {notifByChannel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state">No notifications sent yet</div>
          )}
        </div>

        {/* Top API Endpoints */}
        <div className="chart-card">
          <h3>🔥 Top API Endpoints</h3>
          {topEndpoints.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topEndpoints}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="endpoint" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state">No API calls recorded</div>
          )}
        </div>
      </div>

      {/* System Health */}
      {systemStatus && (
        <div className="system-health-card">
          <h3>⚡ System Health</h3>
          <div className="health-grid">
            <HealthItem icon="💾" label="Memory Usage" value={systemStatus.memory?.percentage ? `${systemStatus.memory.percentage}%` : 'N/A'} />
            <HealthItem icon="🗄️" label="Database" value={systemStatus.database?.state || 'Unknown'} />
            <HealthItem icon="📢" label="Notification Channels" value={systemStatus.notificationChannels ? `${systemStatus.notificationChannels.enabled || 0}/${systemStatus.notificationChannels.total || 0}` : 'N/A'} />
            <HealthItem icon="⚙️" label="Background Jobs" value={systemStatus.backgroundJobs ? `${systemStatus.backgroundJobs.enabled || 0}/${systemStatus.backgroundJobs.total || 0}` : 'N/A'} />
            <HealthItem icon="🔄" label="Order Statuses" value={systemStatus.orderStatuses?.total || 0} />
          </div>
        </div>
      )}
    </div>
  );
};

// Logs Tab Component
const LogsTab = ({ logs, pagination, page, setPage }) => {
  return (
    <div className="logs-tab">
      <div className="logs-header">
        <h3>📝 Action Logs ({pagination?.total || 0} total)</h3>
      </div>

      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Event Type</th>
              <th>Actor</th>
              <th>Target</th>
              <th>Status</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, index) => (
              <tr key={index}>
                <td>{new Date(log.timestamp).toLocaleString()}</td>
                <td><span className="event-type-badge">{log.eventType}</span></td>
                <td>
                  {log.actor?.userId || 'System'}
                  <br />
                  <small>{log.actor?.userType || '-'}</small>
                </td>
                <td>
                  {log.target?.resourceType || '-'}
                  <br />
                  <small>{log.target?.resourceId?.substring(0, 8) || '-'}</small>
                </td>
                <td>
                  <span className={`status-badge ${log.response?.statusCode >= 400 ? 'error' : 'success'}`}>
                    {log.response?.statusCode || 'N/A'}
                  </span>
                </td>
                <td>
                  <button className="details-btn" onClick={() => alert(JSON.stringify(log, null, 2))}>
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {pagination.pages}</span>
          <button 
            onClick={() => setPage(page + 1)}
            disabled={page === pagination.pages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Notifications Tab Component
const NotificationsTab = ({ data }) => {
  // Add safety checks for data
  if (!data) {
    return <div className="empty-state">No notification data available</div>;
  }

  const channelData = Object.entries(data.byChannel || {}).map(([channel, stats]) => ({
    channel,
    ...stats
  }));

  return (
    <div className="notifications-tab">
      <div className="notif-summary">
        <h3>📧 Notification Analytics</h3>
        <p className="total-count">Total: {data.totalNotifications || 0} notifications</p>
      </div>

      {/* By Channel Stats */}
      <div className="channel-stats-grid">
        {channelData && channelData.length > 0 ? (
          channelData.map((ch) => (
            <div key={ch.channel} className="channel-stat-card">
              <h4>{ch.channel.toUpperCase()}</h4>
              <div className="channel-metrics">
                <div className="metric">
                  <span className="label">Total</span>
                  <span className="value">{ch.total || 0}</span>
                </div>
                <div className="metric success">
                  <span className="label">Sent</span>
                  <span className="value">{ch.sent || 0}</span>
                </div>
                <div className="metric error">
                  <span className="label">Failed</span>
                  <span className="value">{ch.failed || 0}</span>
                </div>
                <div className="metric pending">
                  <span className="label">Pending</span>
                  <span className="value">{ch.pending || 0}</span>
                </div>
              </div>
              <div className="success-rate">
                Success Rate: {ch.total > 0 ? ((ch.sent / ch.total) * 100).toFixed(1) : 0}%
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">No channel statistics available</div>
        )}
      </div>

      {/* Daily Trend */}
      <div className="chart-card">
        <h3>📈 Daily Notification Trend</h3>
        {data.dailyBreakdown && data.dailyBreakdown.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.dailyBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#00C49F" fill="#00C49F" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty-state">No daily data available</div>
        )}
      </div>

      {/* Channel Status */}
      <div className="channels-status">
        <h3>🔔 Channel Status</h3>
        <div className="channels-list">
          {data.channels && data.channels.length > 0 ? (
            data.channels.map((ch) => (
              <div key={ch.name} className={`channel-item ${ch?.enabled ? 'enabled' : 'disabled'}`}>
                <span className="channel-name">{ch.name}</span>
                <span className={`status-indicator ${ch?.enabled ? 'active' : 'inactive'}`}>
                  {ch?.enabled ? '✅ Enabled' : '❌ Disabled'}
                </span>
                <span className="priority">Priority: {ch?.priority || 'N/A'}</span>
              </div>
            ))
          ) : (
            <div className="empty-state">No channel data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab = ({ data }) => {
  // Add safety checks for data
  if (!data || !data.platform || !data.buyers || !data.sellers) {
    return <div className="empty-state">No analytics data available</div>;
  }

  return (
    <div className="analytics-tab">
      {/* Platform Metrics */}
      <div className="analytics-section">
        <h3>🌐 Platform Overview</h3>
        <div className="platform-metrics">
          <div className="metric-card">
            <h4>Revenue</h4>
            <p className="big-number">${data.platform.revenue?.total?.toFixed(2) || '0.00'}</p>
            <small>Avg: ${data.platform.revenue?.average?.toFixed(2) || '0.00'}</small>
          </div>
          <div className="metric-card">
            <h4>Orders</h4>
            <p className="big-number">{data.platform.orders?.total || 0}</p>
            <small>Completed: {data.platform.orders?.completed || 0}</small>
          </div>
          <div className="metric-card">
            <h4>Users</h4>
            <p className="big-number">{data.platform.users?.total || 0}</p>
            <small>Buyers: {data.platform.users?.buyers || 0} | Sellers: {data.platform.users?.sellers || 0}</small>
          </div>
          <div className="metric-card">
            <h4>Products</h4>
            <p className="big-number">{data.platform.products?.total || 0}</p>
            <small>Active: {data.platform.products?.active || 0}</small>
          </div>
        </div>
      </div>

      {/* Top Buyers */}
      <div className="analytics-section">
        <h3>🛍️ Top Buyers</h3>
        <div className="buyers-table">
          {data.buyers && data.buyers.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Orders</th>
                  <th>Total Spent</th>
                  <th>Avg Order</th>
                  <th>Favorite Categories</th>
                </tr>
              </thead>
              <tbody>
                {data.buyers.map((buyer) => (
                  <tr key={buyer.buyerId}>
                    <td>
                      {buyer.name}
                      <br />
                      <small>{buyer.email}</small>
                    </td>
                    <td>{buyer.totalOrders || 0}</td>
                    <td>${buyer.totalSpent?.toFixed(2) || '0.00'}</td>
                    <td>${buyer.avgOrderValue?.toFixed(2) || '0.00'}</td>
                    <td>
                      {buyer.favoriteCategories?.slice(0, 3).map(cat => cat.category).join(', ') || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">No buyer data available</div>
          )}
        </div>
      </div>

      {/* Top Sellers */}
      <div className="analytics-section">
        <h3>🏪 Top Sellers</h3>
        <div className="sellers-table">
          {data.sellers && data.sellers.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Orders</th>
                  <th>Revenue</th>
                  <th>Completion Rate</th>
                  <th>Avg Processing</th>
                </tr>
              </thead>
              <tbody>
                {data.sellers.map((seller) => (
                  <tr key={seller.sellerId}>
                    <td>
                      {seller.name}
                      <br />
                      <small>{seller.email}</small>
                    </td>
                    <td>{seller.totalOrders || 0}</td>
                    <td>${seller.revenue?.toFixed(2) || '0.00'}</td>
                    <td>{seller.completionRate?.toFixed(1) || '0'}%</td>
                    <td>{seller.avgProcessingDays?.toFixed(1) || '0'} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">No seller data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Jobs Tab Component
const JobsTab = ({ data }) => {
  // Add safety checks for data
  if (!data || !data.jobs) {
    return <div className="empty-state">No background jobs data available</div>;
  }

  return (
    <div className="jobs-tab">
      <div className="jobs-summary">
        <h3>⚙️ Background Jobs</h3>
        <div className="jobs-stats">
          <div className="stat">
            <span className="label">Total Jobs</span>
            <span className="value">{data.totalJobs || 0}</span>
          </div>
          <div className="stat">
            <span className="label">Enabled</span>
            <span className="value">{data.enabledJobs || 0}</span>
          </div>
          <div className="stat">
            <span className="label">Running</span>
            <span className="value">{data.runningJobs || 0}</span>
          </div>
        </div>
      </div>

      <div className="jobs-list">
        {data.jobs && data.jobs.length > 0 ? (
          data.jobs.map((job) => (
            <div key={job.name} className={`job-card ${job?.enabled ? 'enabled' : 'disabled'}`}>
              <div className="job-header">
                <h4>{job.name}</h4>
                <span className={`job-status ${job?.isRunning ? 'running' : ''}`}>
                  {job?.isRunning ? '🔄 Running' : job?.enabled ? '✅ Enabled' : '❌ Disabled'}
                </span>
              </div>
              <p className="job-description">{job.description || 'No description'}</p>
              <div className="job-details">
                <div className="detail">
                  <span className="label">Schedule:</span>
                  <span className="value">{job.schedule || 'Event-driven'}</span>
                </div>
                <div className="detail">
                  <span className="label">Last Run:</span>
                  <span className="value">{job.lastRun ? new Date(job.lastRun).toLocaleString() : 'Never'}</span>
                </div>
                <div className="detail">
                  <span className="label">Next Run:</span>
                  <span className="value">{job.nextRun ? new Date(job.nextRun).toLocaleString() : 'N/A'}</span>
                </div>
                <div className="detail">
                  <span className="label">Run Count:</span>
                  <span className="value">{job.runCount || 0}</span>
                </div>
                <div className="detail">
                  <span className="label">Failures:</span>
                  <span className="value error">{job.failureCount || 0}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">No jobs configured</div>
        )}
      </div>
    </div>
  );
};

// Billing Tab Component
const BillingTab = ({ data }) => {
  // Add safety checks for data
  if (!data || !data.usage || !data.costs) {
    return <div className="empty-state">No billing data available</div>;
  }

  const usageData = [
    { name: 'API Calls', value: data.usage.apiCalls || 0, cost: data.costs.apiCalls || 0, color: '#0088FE' },
    { name: 'Emails', value: data.usage.emailsSent || 0, cost: data.costs.emails || 0, color: '#00C49F' },
    { name: 'SMS', value: data.usage.smsSent || 0, cost: data.costs.sms || 0, color: '#FFBB28' },
    { name: 'Push Notifications', value: data.usage.pushNotifications || 0, cost: data.costs.push || 0, color: '#FF8042' }
  ];

  return (
    <div className="billing-tab">
      <div className="billing-header">
        <h3>💰 Usage-Based Billing</h3>
        <div className="total-cost">
          <span className="label">Total Cost</span>
          <span className="amount">${data.totalCost || 0}</span>
        </div>
      </div>

      {/* Usage Breakdown */}
      <div className="usage-grid">
        {usageData.map((item) => (
          <div key={item.name} className="usage-card" style={{ borderLeft: `4px solid ${item.color}` }}>
            <h4>{item.name}</h4>
            <div className="usage-stats">
              <div className="stat">
                <span className="label">Usage</span>
                <span className="value">{item.value.toLocaleString()}</span>
              </div>
              <div className="stat">
                <span className="label">Cost</span>
                <span className="value">${item.cost.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cost Distribution */}
      <div className="chart-card">
        <h3>📊 Cost Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={usageData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, cost }) => `${name}: $${cost.toFixed(2)}`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="cost"
            >
              {usageData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Pricing Table */}
      <div className="pricing-section">
        <h3>💵 Pricing Structure</h3>
        <table className="pricing-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Unit Cost</th>
              <th>Usage</th>
              <th>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>API Calls</td>
              <td>${data.pricing.apiCallCost}</td>
              <td>{data.usage.apiCalls.toLocaleString()}</td>
              <td>${data.costs.apiCalls.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Email Notifications</td>
              <td>${data.pricing.emailCost}</td>
              <td>{data.usage.emailsSent.toLocaleString()}</td>
              <td>${data.costs.emails.toFixed(2)}</td>
            </tr>
            <tr>
              <td>SMS Notifications</td>
              <td>${data.pricing.smsCost}</td>
              <td>{data.usage.smsSent.toLocaleString()}</td>
              <td>${data.costs.sms.toFixed(2)}</td>
            </tr>
            <tr>
              <td>Push Notifications</td>
              <td>${data.pricing.pushCost}</td>
              <td>{data.usage.pushNotifications.toLocaleString()}</td>
              <td>${data.costs.push.toFixed(2)}</td>
            </tr>
            <tr className="total-row">
              <td colSpan="3"><strong>Total</strong></td>
              <td><strong>${data.totalCost}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Billing Info */}
      <div className="billing-info">
        <p>💡 <strong>Usage-based pricing</strong> - You only pay for what you use</p>
        <p>📅 Billing period: {new Date(data.dateRange.start).toLocaleDateString()} - {new Date(data.dateRange.end).toLocaleDateString()}</p>
      </div>
    </div>
  );
};

// Helper Components
const MetricCard = ({ icon, title, value, color, subtitle }) => (
  <div className="metric-card" style={{ borderLeft: `4px solid ${color}` }}>
    <div className="metric-icon">{icon}</div>
    <div className="metric-content">
      <h4>{title}</h4>
      <p className="metric-value">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {subtitle && <small>{subtitle}</small>}
    </div>
  </div>
);

const HealthItem = ({ icon, label, value }) => (
  <div className="health-item">
    <span className="health-icon">{icon}</span>
    <div className="health-content">
      <span className="health-label">{label}</span>
      <span className="health-value">{value}</span>
    </div>
  </div>
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export default MonitoringDashboard;
